import { produce } from 'immer'
import {
  SET_EXPENSE_RECEIPT_ATTACHMENT_IDS,
  SetExpenseReceiptAttachmentIdsAction,
} from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import { addExpenseRouteProps } from 'src/navigation/routeProps'
import {
  GET_EXPENSE_REPORT_FAILURE,
  GET_EXPENSE_REPORT_INIT,
  GET_EXPENSE_REPORT_SUCCESS,
  GetExpenseReportActions,
  POST_CREATE_EXPENSE_FAILURE,
  POST_CREATE_EXPENSE_INIT,
  POST_CREATE_EXPENSE_SUCCESS,
  PostCreateExpenseActions,
} from 'src/redux/actions/data/expenses.actions'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions
} from 'src/redux/actions/data/receiptAttachments.actions'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface AddExpenseState {
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  fetchSuccess: boolean
  fetchFailure: boolean
  postingExpense: boolean
  errorDetailMap?: ErrorDetailMap<string>
  topLevelError?: string
}

export const initialState: AddExpenseState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  fetchSuccess: false,
  fetchFailure: false,
  postingExpense: false,
}

export type Actions =
  | GetExpenseReportActions
  | UploadAttachmentActions
  | SetExpenseReceiptAttachmentIdsAction
  | PostCreateExpenseActions

const CoreAddExpenseReducer = (
  state = initialState,
  action: Actions,
): AddExpenseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_EXPENSE_REPORT_INIT: {
        return draft
      }

      case GET_EXPENSE_REPORT_FAILURE: {
        draft.fetchFailure = true
        return draft
      }

      case GET_EXPENSE_REPORT_SUCCESS: {
        draft.fetchSuccess = true
        return draft
      }

      case POST_CREATE_EXPENSE_INIT: {
        draft.postingExpense = true
        draft.errorDetailMap = undefined
        draft.topLevelError = undefined
        return draft
      }

      case POST_CREATE_EXPENSE_SUCCESS: {
        draft.postingExpense = false
        return draft
      }

      case POST_CREATE_EXPENSE_FAILURE: {
        draft.postingExpense = false
        const errors = parseBackendErrors(action.payload)
        draft.errorDetailMap = errors.errorDetailMap
        draft.topLevelError = errors.topLevelError
        return draft
      }

      case SET_EXPENSE_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }
    }
  })
}

const AddExpenseReducer = createFlyoutReducer(
  addExpenseRouteProps,
  initialState,
  CoreAddExpenseReducer,
)

export default AddExpenseReducer
